export type Priority = 'high' | 'medium' | 'low';

export interface PriorityTask {
  priority: Priority;
}

export interface QueueRunnerOptions<TTask extends PriorityTask, TResult> {
  tasks: TTask[];
  concurrency: number;
  worker: (task: TTask) => Promise<TResult>;
  onTaskSettled?: (task: TTask, result: TResult | null, error: unknown | null) => void;
  pollIntervalMs?: number;
  shouldStop?: () => boolean;
}

export interface QueueRunnerOutcome<TTask extends PriorityTask, TResult> {
  total: number;
  processed: number;
  results: Array<{ task: TTask; result: TResult | null; error: unknown | null }>;
}

const PRIORITY_ORDER: Record<Priority, number> = { high: 1, medium: 2, low: 3 };

export function sortByPriority<T extends PriorityTask>(tasks: T[]): T[] {
  return [...tasks].sort((a, b) => PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority]);
}

export async function runPriorityConcurrencyQueue<
  TTask extends PriorityTask,
  TResult,
>(options: QueueRunnerOptions<TTask, TResult>): Promise<QueueRunnerOutcome<TTask, TResult>> {
  const { tasks, concurrency, worker, onTaskSettled, pollIntervalMs = 50, shouldStop } = options;

  const queue = sortByPriority(tasks);
  const total = queue.length;
  const results: QueueRunnerOutcome<TTask, TResult>['results'] = [];
  let active = 0;

  const launch = (task: TTask) => {
    active++;
    worker(task)
      .then((result) => {
        results.push({ task, result, error: null });
        onTaskSettled?.(task, result, null);
      })
      .catch((error) => {
        results.push({ task, result: null, error });
        onTaskSettled?.(task, null, error);
      })
      .finally(() => {
        active--;
      });
  };

  while (queue.length > 0 || active > 0) {
    if (shouldStop?.()) {
      queue.length = 0;
      break;
    }
    while (active < concurrency && queue.length > 0) {
      const task = queue.shift();
      if (task) launch(task);
    }
    await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
  }

  while (active > 0) {
    await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
  }

  return { total, processed: results.length, results };
}
